/**
 * QWERTY musical-typing layout (Logic / GarageBand style, extended range).
 * White: A S D F G H J K L ; ' · Black: W E T Y U O P
 * Offsets are semitones from C of the active octave.
 */
export const PIANO_WHITE_KEYS = [
  "a",
  "s",
  "d",
  "f",
  "g",
  "h",
  "j",
  "k",
  "l",
  ";",
  "'",
] as const;

/** Semitone offsets from C for each white key (spans into the next octave). */
export const PIANO_WHITE_OFFSETS = [0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17] as const;

export const PIANO_BLACK_OFFSETS: Readonly<Record<string, number>> = {
  w: 1,
  e: 3,
  t: 6,
  y: 8,
  u: 10,
  o: 13,
  p: 15,
};

export const PIANO_BLACK_KEYS = Object.keys(PIANO_BLACK_OFFSETS);

/** Semitone from C for a typing key, or undefined if not a piano key. */
export function pianoKeySemitone(key: string): number | undefined {
  const k = key.length === 1 ? key.toLowerCase() : key;
  const whiteIdx = (PIANO_WHITE_KEYS as readonly string[]).indexOf(k);
  if (whiteIdx >= 0) {
    return PIANO_WHITE_OFFSETS[whiteIdx];
  }
  return PIANO_BLACK_OFFSETS[k];
}

/** MIDI note for a piano-typing key at tracker octave (C4 = 60 when octave is 4). */
export function pianoKeyMidi(key: string, octave: number): number | undefined {
  const semi = pianoKeySemitone(key);
  if (semi === undefined) {
    return undefined;
  }
  return (octave + 1) * 12 + semi;
}

/**
 * Keys from the next octave (K L ; ' and black O P) must not bump the
 * remembered typing octave — otherwise re-pressing them climbs forever.
 */
export function pianoKeyRemembersOctave(key: string): boolean {
  const semi = pianoKeySemitone(key);
  return semi !== undefined && semi < 12;
}

/** Build key→MIDI map for on-screen piano typing (base C + octave shift). */
export function buildPianoTypingMap(baseMidi: number, octaveShift: number): Map<string, number> {
  const map = new Map<string, number>();
  const root = baseMidi + octaveShift * 12;
  for (let i = 0; i < PIANO_WHITE_KEYS.length; i++) {
    map.set(PIANO_WHITE_KEYS[i]!, root + PIANO_WHITE_OFFSETS[i]!);
  }
  for (const [key, offset] of Object.entries(PIANO_BLACK_OFFSETS)) {
    map.set(key, root + offset);
  }
  return map;
}
